import { NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { isValidPhoneNumber, phoneNumberValidator } from '@core';
import { ButtonActivity, Contact, DonationMeta, ENVIRONMENT, Environment, Lead, PageActivity } from '@domains';
import { PageFooterComponent } from '@donor/components/page-footer/page-footer.component';
import {
  ConfirmDialogComponent,
  DesignService,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  ResponsiveService,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';

import { AppService } from '../../../../app.service';
import { CreateProgressComponent } from '../../../../components/create-progress/create-progress.component';
import { InfoFormComponent } from '../../../../components/info-form/info-form.component';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { OptimizelyService } from '../../../../optimizely.service';
import { FlowService } from '../../donor-flow.service';
import { BaseFlowStepComponent } from '../base-flow-step.component';

const NAME_REGEX = /^[^\s]+.*$/;
const EMAIL_REGEX = /^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;

@Component({
  selector: 'app-flow-additional-information',
  templateUrl: './flow-additional-information.component.html',
  styleUrls: ['./flow-additional-information.component.scss'],
  imports: [
    NgTemplateOutlet,
    PageHeaderComponent,
    PageFooterComponent,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    InfoFormComponent,
    MatCheckbox,
    LaddaModule,
    CreateProgressComponent,
    FxFlexDirective,
  ],
})
export class FlowAdditionalInformationComponent extends BaseFlowStepComponent implements OnInit, AfterViewInit {
  public form!: FormGroup<{
    donor: FormGroup<{
      name: FormControl<string>;
      email: FormControl<string>;
      phone: FormControl<string>;
    }>;
    address?: FormGroup<{
      state: FormControl<string>;
    }>;
    instructions: FormControl<string | null>;
    donationItems: FormControl<string | null>;
  }>;
  readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  smsApproval = false;

  constructor(
    override flowService: FlowService,
    override appService: AppService,
    override router: Router,
    override route: ActivatedRoute,
    override designService: DesignService,
    override responsiveService: ResponsiveService,
    protected dialog: MatDialog,
    protected override optimizelyService: OptimizelyService,
    @Inject(ENVIRONMENT) protected override environment: Environment,
  ) {
    super(flowService, appService, router, route, designService, responsiveService, optimizelyService, environment);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    const currentSmsApproval = this.flowService?.lead?.meta?.smsApproval;
    const checkboxFlag = this.optimizelyService.marketingVariation === 'sms_checkbox_true';
    this.smsApproval = currentSmsApproval === undefined ? checkboxFlag : currentSmsApproval;

    this.form = new FormGroup({
      donor: new FormGroup({
        name: new FormControl(this.flowService.lead.donor?.name || '', [Validators.required, Validators.pattern(NAME_REGEX)]),
        email: new FormControl(this.flowService.lead.donor?.email || '', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
        phone: new FormControl(this.flowService.lead.donor?.phone || '', [Validators.required, phoneNumberValidator()]),
      }),
      instructions: new FormControl(this.flowService.lead.instructions),
      donationItems: new FormControl(this.flowService.lead.donationItems),
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.nameInput()?.nativeElement.focus();
    });
  }

  static override isCompleted(lead: Lead) {
    return (
      !!lead.donor.email &&
      !!lead.donor.name &&
      !!lead.donor.phone &&
      NAME_REGEX.test(lead.donor.name) &&
      EMAIL_REGEX.test(lead.donor.email) &&
      isValidPhoneNumber(lead.donor.phone)
    );
  }

  override isValid(): boolean {
    return !this.form.invalid;
  }

  override getFromValue(): Partial<Lead> {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.SMS_CHECKBOX, this.smsApproval ? 'checked' : 'unchecked').subscribe();
    return {
      donor: new Contact({
        name: this.form.get('donor').getRawValue().name,
        email: this.form.get('donor').getRawValue().email,
        phone: this.form.get('donor').getRawValue().phone.replace('\s', '').replace('(', '').replace(')', '').replace('-', ''),
      }),
      instructions: this.form.get('instructions').value,
      donationItems: this.form.get('donationItems').value,
      meta: new DonationMeta({
        ...this.flowService.lead.meta,
        smsApproval: this.smsApproval,
      }),
    };
  }

  override showError(): void {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
  }

  override saveButtonActivity(): ButtonActivity {
    return ButtonActivity.DONOR_INFO_SUBMIT;
  }

  override pageVisitActivity(): PageActivity {
    return PageActivity.DONOR_INFO_PAGE;
  }

  smsDialog(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '100vw',
      data: {
        message: `By providing your phone number and checking this box, you expressly agree to authorize Resupply Inc. to contact you by text via the mobile phone number provided to schedule your donation pickup and/or manage your pickup details. For any questions, text us at +1 (857) 342-4937. You may opt out of specific updates at any time by replying STOP to any message received. Messaging and data rates may apply. See our Terms of Service and Privacy Policy <a href="https://resupplyapp.com/donor-terms-of-use/" target="_blank">here</a> for more details about how Resupply uses SMS and voice communications.`,
        hideNo: true,
        yes: 'Close',
        color: 'primary',
      },
    });
  }
}
